import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Configure axios globally for credentials
axios.defaults.withCredentials = true;

// ====================================================================
// Axios interceptor: su 401 prova /api/auth/refresh una sola volta,
// se ha successo ritenta la richiesta originale; altrimenti lascia
// passare l'errore così l'AuthContext può fare logout.
// Evita di intercettare /auth/login, /auth/refresh, /auth/me (sono i
// trigger naturali dell'auth e farebbero loop).
// ====================================================================
let refreshPromise = null; // dedupe concurrent refresh calls

function shouldAttemptRefresh(config) {
    if (!config || config._skipAuthRefresh) return false;
    const url = config.url || '';
    if (url.includes('/auth/login')) return false;
    if (url.includes('/auth/refresh')) return false;
    if (url.includes('/auth/logout')) return false;
    return true;
}

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { response, config } = error;
        if (!response || response.status !== 401 || !shouldAttemptRefresh(config) || config._retry) {
            return Promise.reject(error);
        }
        config._retry = true;
        try {
            if (!refreshPromise) {
                refreshPromise = axios.post(`${API}/auth/refresh`, {}, { _skipAuthRefresh: true })
                    .finally(() => { refreshPromise = null; });
            }
            await refreshPromise;
            return axios(config); // retry original request
        } catch (refreshErr) {
            // Refresh fallito → notifica all'AuthContext via evento custom
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('auth:session-expired'));
            }
            return Promise.reject(error);
        }
    }
);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = checking, null = not authenticated, object = authenticated
    const [loading, setLoading] = useState(true);
    const hasChecked = useRef(false);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`);
            setUser(data);
            if (typeof window !== 'undefined') window.dispatchEvent(new Event('branding:refresh'));
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
            hasChecked.current = true;
        }
    }, []);

    useEffect(() => {
        if (!hasChecked.current) {
            checkAuth();
        }
    }, [checkAuth]);

    // Listener globale per "sessione scaduta": pulisce stato + redirect a /login
    useEffect(() => {
        const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/superadmin', '/signup', '/payment', '/terms', '/privacy', '/manuale', '/dpa', '/sub-responsabili', '/integrazione-fatture-in-cloud'];
        const handler = () => {
            setUser(null);
            const path = typeof window !== 'undefined' ? window.location.pathname : '';
            if (path && !PUBLIC_PATHS.some((p) => path.startsWith(p))) {
                window.location.href = '/login';
            }
        };
        window.addEventListener('auth:session-expired', handler);
        return () => window.removeEventListener('auth:session-expired', handler);
    }, []);

    const login = async (username, password, rememberMe = false) => {
        const { data } = await axios.post(`${API}/auth/login`, { username, password, remember_me: rememberMe });
        setUser(data);
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('branding:refresh'));
        return data;
    };

    const demoLogin = async (role = 'admin') => {
        const { data } = await axios.post(`${API}/auth/demo-login`, { role });
        setUser(data);
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('branding:refresh'));
        return data;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { _skipAuthRefresh: true });
        } catch {
            // ignore
        }
        setUser(null);
    };

    const changePassword = async (newPassword, email = null) => {
        await axios.post(`${API}/auth/change-password`, { new_password: newPassword, email });
        await checkAuth();
    };

    const requestPasswordCode = async (newPassword, email = null) => {
        const { data } = await axios.post(`${API}/auth/change-password/request-code`, { new_password: newPassword, email });
        if (data?.demo_bypass) await checkAuth();
        return data;
    };

    const verifyPasswordCode = async (code) => {
        await axios.post(`${API}/auth/change-password/verify-code`, { code });
        await checkAuth();
    };

    const forgotPassword = async (email) => {
        await axios.post(`${API}/auth/forgot-password`, { email });
    };

    const resetPassword = async (token, newPassword) => {
        await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, demoLogin, logout, changePassword, requestPasswordCode, verifyPasswordCode, forgotPassword, resetPassword, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
